import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ClockFill, ArrowRight, PersonFill, LightningChargeFill } from "react-bootstrap-icons";
import { carTitle, formatPrice, coverImage } from "../../common/format";
import { currentLanguage } from "../../common/ui/notify";
import { t } from "./auctionI18n";

// A self-playing "live auction" simulation for the landing page: a car goes on
// the block, the bid climbs in real time as (illustrative) bidders come in, the
// countdown resets on every bid, then it sells and moves to the next car. It is
// clearly framed as a demo — it uses the real event's cars but generated bids.

// Illustrative bidder names for the demo feed, in the active language so the
// simulation reads naturally whatever locale the visitor is on.
const NAME_POOLS = {
    geo: {
        first: ["გიორგი", "ნინო", "დავით", "მარიამ", "ლევან", "ანა", "ზურაბ", "სალომე",
                "ირაკლი", "თამარ", "ბექა", "ქეთი", "ნიკა", "ელენე", "საბა", "ლიკა",
                "გურამ", "მაია", "ლაშა", "ნათია", "გიო", "თეო"],
        last: ["გ.", "ქ.", "მ.", "ბ.", "ც.", "კ.", "თ.", "ლ.", "ჯ.", "ს.", "ძ.", "შ.", "ხ."],
    },
    en: {
        first: ["James", "Mary", "John", "Patricia", "Robert", "Jennifer", "Michael", "Linda",
                "David", "Elizabeth", "William", "Susan", "Richard", "Jessica", "Thomas", "Sarah",
                "Daniel", "Karen", "Mark", "Nancy", "Paul", "Lisa"],
        last: ["B.", "C.", "D.", "F.", "G.", "H.", "K.", "L.", "M.", "P.", "R.", "S.", "W."],
    },
    ru: {
        first: ["Иван", "Анна", "Дмитрий", "Мария", "Сергей", "Елена", "Алексей", "Ольга",
                "Андрей", "Наталья", "Максим", "Татьяна", "Николай", "Ирина", "Павел", "Светлана",
                "Егор", "Юлия", "Роман", "Екатерина", "Денис", "Марина"],
        last: ["И.", "П.", "М.", "Б.", "К.", "С.", "Т.", "Л.", "Г.", "Ф.", "Р.", "Ш.", "Х."],
    },
    am: {
        first: ["Արամ", "Անի", "Դավիթ", "Մարիամ", "Լևոն", "Նարե", "Հայկ", "Լիլիթ",
                "Վահան", "Անուշ", "Գոռ", "Մարի", "Տիգրան", "Ելենա", "Սարգիս", "Նունե",
                "Արմեն", "Մանե", "Վարդան", "Սիրան", "Հովիկ", "Թամար"],
        last: ["Հ.", "Ք.", "Մ.", "Բ.", "Ց.", "Կ.", "Թ.", "Լ.", "Ջ.", "Ս.", "Ձ.", "Շ.", "Խ."],
    },
};
const POOL = NAME_POOLS[currentLanguage()] || NAME_POOLS.geo;
const FIRST = POOL.first;
const LAST = POOL.last;

const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];
let lastFirst = null;
const bidderName = () => {
    let first = pick(FIRST);
    for (let i = 0; i < 5 && first === lastFirst; i++) first = pick(FIRST);
    lastFirst = first;
    return `${first} ${pick(LAST)}`;
};
const stepFor = (p) => {
    if (p < 5000) return pick([200, 250, 300, 500]);
    if (p < 15000) return pick([500, 750, 1000]);
    if (p < 40000) return pick([1000, 1500, 2000]);
    return pick([2000, 2500, 5000]);
};

const START_SECONDS = 9; // opening countdown
const BID_WINDOW = 6;     // each new bid resets the timer to this
const SOLD_HOLD = 2600;   // how long the "SOLD" state lingers

const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const LiveAuctionDemo = ({ cars = [], event, loggedIn = false }) => {
    const list = cars.filter((c) => c && (c.start_price != null || c.current_price != null));
    const [idx, setIdx] = useState(0);
    const [bid, setBid] = useState(0);
    const [timeLeft, setTimeLeft] = useState(START_SECONDS);
    const [feed, setFeed] = useState([]);
    const [status, setStatus] = useState("live"); // "live" | "sold"
    const [winner, setWinner] = useState(null);
    const deadlineRef = useRef(0);
    const timeoutsRef = useRef([]);

    const count = list.length;
    const car = count ? list[idx % count] : null;
    const currency = (car && car.currency) || (event && event.currency) || "GEL";
    const openingOf = (c) => {
        const cur = Number(c.current_price);
        const start = Number(c.start_price);
        return cur > 0 && cur > start ? cur : (start || cur || 0);
    };

    useEffect(() => {
        if (!count || !car) return undefined;
        const clearTimers = () => { timeoutsRef.current.forEach(clearTimeout); timeoutsRef.current = []; };

        const opening = openingOf(car);
        setBid(opening);
        setFeed([]);
        setStatus("live");
        setWinner(null);
        setTimeLeft(START_SECONDS);

        // Respect reduced motion: show a filled, static snapshot and don't cycle.
        if (reducedMotion) {
            let b = opening;
            const rows = [];
            for (let i = 0; i < 3; i++) { b += stepFor(b); rows.unshift({ id: i, name: bidderName(), amount: b }); }
            setBid(b);
            setFeed(rows);
            return undefined;
        }

        let current = opening;
        let lastName = null;
        deadlineRef.current = Date.now() + START_SECONDS * 1000;

        const numBids = 4 + Math.floor(Math.random() * 3); // 4..6 bids
        let at = 900 + Math.random() * 500;
        for (let i = 0; i < numBids; i++) {
            timeoutsRef.current.push(setTimeout(() => {
                current += stepFor(current);
                lastName = bidderName();
                deadlineRef.current = Date.now() + BID_WINDOW * 1000; // timer resets on each bid
                setBid(current);
                setFeed((prev) => [{ id: Date.now() + Math.random(), name: lastName, amount: current }, ...prev].slice(0, 4));
            }, at));
            at += 1300 + Math.random() * 1300; // 1.3s..2.6s apart
        }

        const tick = setInterval(() => {
            const left = Math.max(0, deadlineRef.current - Date.now());
            setTimeLeft(Math.ceil(left / 1000));
            if (left <= 0) {
                clearInterval(tick);
                setStatus("sold");
                setWinner(lastName);
                timeoutsRef.current.push(setTimeout(() => setIdx((p) => (p + 1) % count), SOLD_HOLD));
            }
        }, 200);

        return () => { clearInterval(tick); clearTimers(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idx, count]);

    if (!count || !car) return null;

    const img = coverImage(car);
    const lotNo = car.position || (idx % count) + 1;
    const urgent = status === "live" && timeLeft <= 3;
    const progress = Math.max(0, Math.min(100, (timeLeft / START_SECONDS) * 100));

    return (
        <section className="container al-section ald-section">
            <div className="al-section-head">
                <h2>{t("Live დემო", "Live demo", "Живое демо", "Կենդանի դեմո")}</h2>
            </div>

            <div className={`ald-stage ${status === "sold" ? "is-sold" : ""} ${urgent ? "is-urgent" : ""}`}>
                <div className="ald-car" style={img ? { backgroundImage: `url('${img}')` } : {}}>
                    <div className="ald-car-top">
                        <span className="ald-lot">{t("ლოტი", "Lot", "Лот", "Լոտ")} #{lotNo}</span>
                        <span className="ald-live"><span className="ald-live-dot" /> LIVE</span>
                    </div>
                    <div className="ald-car-bottom">
                        <strong>{carTitle(car)}</strong>
                    </div>
                    <span className="ald-progress" style={{ width: `${progress}%` }} />

                    {status === "sold" && (
                        <div className="ald-sold">
                            <span className="ald-sold-stamp">{t("გაიყიდა!", "SOLD!", "ПРОДАНО!", "ՎԱՃԱՌՎԱԾ!")}</span>
                            <span className="ald-sold-price">{formatPrice(bid, currency)}</span>
                            {winner && <span className="ald-sold-winner">{t("გამარჯვებული:", "Winner:", "Победитель:", "Հաղթող:")} {winner}</span>}
                        </div>
                    )}
                </div>

                <div className="ald-panel">
                    <div className="ald-panel-top">
                        <div className="ald-bid">
                            <span className="ald-bid-label">{t("მიმდინარე ბიდი", "Current bid", "Текущая ставка", "Ընթացիկ հայտ")}</span>
                            <strong key={bid} className="ald-bid-val">{formatPrice(bid, currency)}</strong>
                        </div>
                        <div className={`ald-clock ${urgent ? "is-urgent" : ""}`}>
                            <ClockFill /> <span>00:{String(timeLeft).padStart(2, "0")}</span>
                        </div>
                    </div>

                    <div className="ald-feed">
                        {feed.length === 0 && (
                            <div className="ald-feed-empty">{t("ელოდება პირველ ბიდს…", "Waiting for the first bid…", "Ожидание первой ставки…", "Սպասում է առաջին հայտին…")}</div>
                        )}
                        {feed.map((f, i) => (
                            <div className={`ald-feed-row ${i === 0 ? "is-new" : ""}`} key={f.id}>
                                <span className="ald-feed-who"><PersonFill /> {f.name}</span>
                                <span className="ald-feed-amt">{formatPrice(f.amount, currency)}</span>
                            </div>
                        ))}
                    </div>

                    <Link to={loggedIn ? (event ? `/auction/event/${event.id}/live` : "/auctions") : "/auction/register"} className="cx-btn cx-btn-primary ald-cta">
                        <LightningChargeFill /> {loggedIn ? t("დადე ბიდი", "Place a bid", "Сделать ставку", "Դրեք հայտ") : t("შემოდი და დადე ბიდი", "Join and place a bid", "Войдите и сделайте ставку", "Միացեք և դրեք հայտ")}
                    </Link>
                </div>
            </div>

            <div className="ald-foot">
                <Link to={event ? `/auction/event/${event.id}` : "/auctions"} className="ald-all">
                    {t("ნახე ყველა ავტომობილი", "See all cars", "Смотреть все авто", "Տեսնել բոլոր մեքենաները")}{count ? ` (${count})` : ""} <ArrowRight />
                </Link>
            </div>
        </section>
    );
};

export default LiveAuctionDemo;
